import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import { useState, useCallback } from 'react';
import '../utils/assertion.esm.js';
import '../utils/ssr.esm.js';
import '../utils/number.esm.js';
import '../utils/array.esm.js';
import '../utils/breakpoint.esm.js';
import '../utils/context.esm.js';
import { dataAttr } from '../utils/dom.esm.js';
import '../utils/tabbable.esm.js';
import '../utils/dom-query.esm.js';
import '../utils/logger.esm.js';
import '../utils/focus.esm.js';
import '../utils/function.esm.js';
import '../utils/keydown.esm.js';
import '../utils/lazy.esm.js';
import '../utils/object.esm.js';
import { mergeRefs } from '../utils/react-helpers.esm.js';
import '../utils/style-props.esm.js';
import '../utils/tailwind.esm.js';

/**
 * useClickable
 *
 * React hook that implements all the interactions of a native `button`
 * component with support for making it focusable even if it's disabled.
 *
 * It can be used with both native button elements or other elements (like `div`).
 */

function useClickable(props) {
  if (props === void 0) {
    props = {};
  }

  var _props = props,
      htmlRef = _props.ref,
      disabled = _props.disabled,
      focusable = _props.focusable,
      _props$clickOnEnter = _props.clickOnEnter,
      clickOnEnter = _props$clickOnEnter === void 0 ? true : _props$clickOnEnter,
      _props$clickOnSpace = _props.clickOnSpace,
      clickOnSpace = _props$clickOnSpace === void 0 ? true : _props$clickOnSpace,
      onMouseDown = _props.onMouseDown,
      onMouseUp = _props.onMouseUp,
      onClick = _props.onClick,
      onKeyDown = _props.onKeyDown,
      onKeyUp = _props.onKeyUp,
      tabIndexProp = _props.tabIndex,
      onMouseOver = _props.onMouseOver,
      rest = _objectWithoutPropertiesLoose(_props, ["ref", "disabled", "focusable", "clickOnEnter", "clickOnSpace", "onMouseDown", "onMouseUp", "onClick", "onKeyDown", "onKeyUp", "tabIndex", "onMouseOver"]); // We'll use this to track if the element is a button element


  var _React$useState = useState(true),
      isButton = _React$useState[0],
      setIsButton = _React$useState[1]; // For custom button implementation, we'll use this to track when
  // we mouse down on the button, to enable use style it's ":active" style


  var _React$useState2 = useState(false),
      isActive = _React$useState2[0],
      setIsActive = _React$useState2[1]; // The ref callback that fires as soon as the dom node is ready


  var refCallback = useCallback(function (node) {
    if (node && node.tagName !== 'BUTTON') {
      setIsButton(false);
    }
  }, []);
  var tabIndex = isButton ? tabIndexProp : tabIndexProp || 0;
  var trulyDisabled = disabled && !focusable;
  var handleClick = useCallback(function (event) {
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    var self = event.currentTarget;
    self.focus();
    onClick == null ? void 0 : onClick(event);
  }, [disabled, onClick]);
  var handleKeyDown = useCallback(function (event) {
    onKeyDown == null ? void 0 : onKeyDown(event);

    if (disabled || event.defaultPrevented || event.metaKey) {
      return;
    }

    var shouldClickOnEnter = clickOnEnter && event.key === 'Enter';
    var shouldClickOnSpace = clickOnSpace && event.key === ' ';

    if (!isButton && shouldClickOnSpace) {
      event.preventDefault();
      setIsActive(true);
      return;
    }

    if (!isButton && shouldClickOnEnter) {
      event.preventDefault();
      var self = event.currentTarget;
      self.click();
    }
  }, [disabled, isButton, onKeyDown, clickOnEnter, clickOnSpace]);
  var handleKeyUp = useCallback(function (event) {
    onKeyUp == null ? void 0 : onKeyUp(event);
    if (disabled || event.defaultPrevented || event.metaKey) return;
    var shouldClickOnSpace = clickOnSpace && event.key === ' ';

    if (!isButton && shouldClickOnSpace) {
      event.preventDefault();
      setIsActive(false);
      var self = event.currentTarget;
      self.click();
    }
  }, [clickOnSpace, isButton, disabled, onKeyUp]);
  var handleMouseDown = useCallback(function (event) {
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    if (!isButton) {
      setIsActive(true);
    }

    onMouseDown == null ? void 0 : onMouseDown(event);
  }, [disabled, isButton, onMouseDown]);
  var handleMouseUp = useCallback(function (event) {
    if (!isButton) {
      setIsActive(false);
    }

    onMouseUp == null ? void 0 : onMouseUp(event);
  }, [onMouseUp, isButton]);
  var handleMouseOver = useCallback(function (event) {
    if (disabled) {
      event.preventDefault();
      return;
    }

    onMouseOver == null ? void 0 : onMouseOver(event);
  }, [disabled, onMouseOver]);
  var ref = mergeRefs(htmlRef, refCallback);

  if (isButton) {
    return _extends({}, rest, {
      ref: ref,
      type: 'button',
      'aria-disabled': trulyDisabled ? undefined : disabled,
      disabled: trulyDisabled,
      onClick: handleClick,
      onMouseDown: onMouseDown,
      onMouseUp: onMouseUp,
      onKeyUp: onKeyUp,
      onKeyDown: onKeyDown,
      onMouseOver: onMouseOver
    });
  }

  return _extends({}, rest, {
    ref: ref,
    role: 'button',
    'data-active': dataAttr(isActive),
    'aria-disabled': !!disabled,
    tabIndex: trulyDisabled ? undefined : tabIndex,
    onClick: handleClick,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onKeyUp: handleKeyUp,
    onKeyDown: handleKeyDown,
    onMouseOver: handleMouseOver
  });
}

export { useClickable };
//# sourceMappingURL=use-clickable.esm.js.map
